import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Menu,
  Select,
  Stack,
  Switch,
  Tabs,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconBuildingBank,
  IconDownload,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useSearchParams } from 'react-router';
import BillingEditModal from 'src/secret-menu/billing/BillingEditModal';
import { formatBillingMonth, nextBillingMonth } from 'src/secret-menu/billing/billingUtils';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { useBillingDetails } from 'src/secret-menu/billing/hooks/useBillingDetails';
import { useBillingExport } from 'src/secret-menu/billing/hooks/useBillingExport';
import {
  useMonumentTerms,
  useRevenueHistory,
  useRevenueOverview,
  type ScenarioPatch,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { useUsageMetrics } from './hooks/useUsageMetrics';
import { useUsageMetricsExport } from './hooks/useUsageMetricsExport';
import FirmDetail from './overview/FirmDetail';
import FirmsPanel from './overview/FirmsPanel';
import { LENS_LABELS, LENS_ORDER, type Lens } from './overview/lenses';
import { buildRevenueModel } from './overview/revenue';
import UsageDashboard from './overview/UsageDashboard';
import { buildUsageModel } from './overview/usageModel';
import KpiHeadline from './revenue/KpiHeadline';
import MonumentDrawer from './revenue/MonumentDrawer';
import RevenueModeller from './revenue/RevenueModeller';
import RevenuePanel from './revenue/RevenuePanel';

const VIEWS = ['overview', 'firms', 'revenue', 'modeller'] as const;
type View = (typeof VIEWS)[number];

/** The open tab lives in the URL, so going back from a firm returns to it and links open on it. */
function useView(): [View, (view: View) => void] {
  const [params, setParams] = useSearchParams();
  const requested = params.get('view');
  const view = VIEWS.find(candidate => candidate === requested) ?? 'overview';
  const setView = (next: View) =>
    setParams(
      current => {
        const updated = new URLSearchParams(current);
        if (next === 'overview') updated.delete('view');
        else updated.set('view', next);
        return updated;
      },
      { replace: true },
    );
  return [view, setView];
}

export default function UsageMetricsTab() {
  const [view, setView] = useView();
  const [includeInternal, setIncludeInternal] = useState(false);
  const [includeMessages, setIncludeMessages] = useState(false);
  const [includeConversations, setIncludeConversations] = useState(false);
  const [month, setMonth] = useState<string | null>(null);
  const [lens, setLens] = useState<Lens>('raw');
  const [selectedFirm, setSelectedFirm] = useState<string | null>(null);
  const [editBilling, setEditBilling] = useState<BillingDetail | null>(null);
  const [editingMonument, setEditingMonument] = useState(false);
  const [scenario, setScenario] = useState<ScenarioPatch>({});

  const {
    rows,
    isLoading: isUsageLoading,
    isError: isUsageError,
  } = useUsageMetrics(includeInternal);
  const { downloadExport, isLoading: isExporting } = useUsageMetricsExport();
  const { downloadExport: downloadBilling, isLoading: isExportingBilling } = useBillingExport();
  const billingMonthLabel = formatBillingMonth(nextBillingMonth());
  const {
    data: billingData,
    isLoading: isBillingLoading,
    isError: isBillingError,
  } = useBillingDetails();
  const revenueOverview = useRevenueOverview(includeInternal);
  const revenueHistory = useRevenueHistory(includeInternal);
  const monumentTerms = useMonumentTerms();
  const revenueQueries = [revenueOverview, revenueHistory, monumentTerms];

  // Billing is joined onto every panel here, so a billing failure has to gate the tab alongside
  // usage - a missing payload is indistinguishable from "no billing configured" once rendered.
  const isRevenueError = revenueQueries.some(query => query.isError);
  const isLoading =
    isUsageLoading || isBillingLoading || revenueQueries.some(query => query.isLoading);
  const isError = isUsageError || isBillingError || isRevenueError;
  const failedSources = [
    ...(isUsageError ? ['usage metrics'] : []),
    ...(isBillingError ? ['billing details'] : []),
    ...(isRevenueError ? ['revenue'] : []),
  ].join(' and ');
  const firmsInScope = (billingData ?? []).filter(detail => includeInternal || !detail.isInternal);

  const today = new Date();
  const model = buildUsageModel(rows ?? [], today);
  const revenue = buildRevenueModel(
    billingData,
    model?.firms.map(entry => entry.name) ?? [],
    includeInternal,
  );

  const activeMonth =
    model && month && model.months.includes(month)
      ? month
      : // The current month even before it ends, so the page opens on what is happening now.
        (model?.partialMonth ?? model?.reportingMonth ?? null);
  const showsUsage = view === 'overview' || view === 'firms';
  const firm =
    model && selectedFirm ? model.firms.find(entry => entry.name === selectedFirm) : undefined;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Switch
          label="Include internal companies"
          checked={includeInternal}
          onChange={event => setIncludeInternal(event.currentTarget.checked)}
        />

        <Group gap="sm" align="flex-end" wrap="wrap">
          {model && activeMonth && showsUsage && (
            <>
              <Select
                label="Month"
                size="xs"
                w={150}
                data={model.months
                  .map(key => ({ value: key, label: model.monthLabels.get(key) ?? key }))
                  .reverse()}
                value={activeMonth}
                onChange={setMonth}
                allowDeselect={false}
              />
              <Select
                label="Lens"
                size="xs"
                w={190}
                data={LENS_ORDER.map(value => ({ value, label: LENS_LABELS[value] }))}
                value={lens}
                onChange={value => setLens((value as Lens | null) ?? 'raw')}
                allowDeselect={false}
              />
            </>
          )}
          <Button
            size="xs"
            variant="light"
            leftSection={<IconBuildingBank size={14} />}
            onClick={() => setEditingMonument(true)}
          >
            Strategic partner
          </Button>
          <Menu position="bottom-end" closeOnItemClick={false} withinPortal>
            <Menu.Target>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconDownload size={14} />}
                rightSection={<IconChevronDown size={14} />}
                loading={isExporting || isExportingBilling}
              >
                Download
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Usage spreadsheet</Menu.Label>
              <Stack gap={6} px="sm" py={4}>
                <Checkbox
                  size="xs"
                  label="Add Siesta messages to totals"
                  checked={includeMessages}
                  onChange={event => setIncludeMessages(event.currentTarget.checked)}
                />
                <Checkbox
                  size="xs"
                  label="Add conversations to totals"
                  checked={includeConversations}
                  onChange={event => setIncludeConversations(event.currentTarget.checked)}
                />
              </Stack>
              <Menu.Item
                leftSection={<IconDownload size={14} />}
                onClick={() =>
                  downloadExport({ includeInternal, includeMessages, includeConversations })
                }
              >
                Usage metrics (xlsx)
              </Menu.Item>
              <Menu.Divider />
              <Menu.Label>Billing</Menu.Label>
              <Menu.Item
                leftSection={<IconDownload size={14} />}
                onClick={() => downloadBilling({ includeInternal })}
              >
                {billingMonthLabel} billing run (xlsx)
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {isLoading && (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      )}

      {isError && (
        <Alert icon={<IconAlertTriangle />} color="red" variant="light">
          Could not load {failedSources}. Try refreshing the page.
        </Alert>
      )}

      {!isLoading && !isError && !model && (
        <Alert icon={<IconInfoCircle />} variant="light">
          No usage data yet.
        </Alert>
      )}

      {!isLoading && !isError && model && activeMonth && firm && (
        <FirmDetail
          model={model}
          firm={firm}
          month={activeMonth}
          lens={lens}
          today={today}
          revenue={revenue}
          onEditBilling={setEditBilling}
          onBack={() => setSelectedFirm(null)}
        />
      )}

      {!isLoading && !isError && model && activeMonth && !firm && (
        <Tabs
          value={view}
          onChange={next => setView((next as View | null) ?? 'overview')}
          keepMounted={false}
        >
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="firms">Firm usage ({model.firms.length})</Tabs.Tab>
            <Tabs.Tab value="revenue">Revenue</Tabs.Tab>
            <Tabs.Tab value="modeller">Revenue modeller</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <UsageDashboard
              model={model}
              headline={
                revenueOverview.data && (
                  <KpiHeadline
                    model={model}
                    month={activeMonth}
                    overview={revenueOverview.data}
                    history={revenueHistory.data ?? []}
                    today={today}
                    firms={firmsInScope}
                    rows={rows ?? []}
                  />
                )
              }
              month={activeMonth}
              lens={lens}
              today={today}
              onSelectFirm={setSelectedFirm}
            />
          </Tabs.Panel>
          <Tabs.Panel value="firms" pt="md">
            <FirmsPanel
              model={model}
              month={activeMonth}
              lens={lens}
              today={today}
              revenue={revenue}
              onSelectFirm={setSelectedFirm}
            />
          </Tabs.Panel>
          <Tabs.Panel value="revenue" pt="md">
            {revenueOverview.data && monumentTerms.data && (
              <RevenuePanel
                overview={revenueOverview.data}
                history={revenueHistory.data ?? []}
                monument={monumentTerms.data}
                firms={firmsInScope}
                today={today}
                onEditFirm={companyId =>
                  setEditBilling(
                    billingData?.find(detail => detail.companyId === companyId) ?? null,
                  )
                }
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="modeller" pt="md">
            {revenueOverview.data && monumentTerms.data && (
              <RevenueModeller
                overview={revenueOverview.data}
                terms={monumentTerms.data}
                includeInternal={includeInternal}
                patch={scenario}
                onPatchChange={setScenario}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      <BillingEditModal billingDetail={editBilling} onClose={() => setEditBilling(null)} />
      {monumentTerms.data && revenueOverview.data && (
        <MonumentDrawer
          opened={editingMonument}
          terms={monumentTerms.data}
          today={revenueOverview.data.monumentToday}
          fullAdoption={revenueOverview.data.monumentFullAdoption}
          onClose={() => setEditingMonument(false)}
        />
      )}
    </Stack>
  );
}
